import { useEffect, useRef, useState } from 'preact/hooks';
import { registerableComponentList } from 'js/registerable-components.js';

declare global {
    interface Window {
        futprintChartInit?: () => void;
    }
}

const fragmentHtmlCache = new Map<string, Promise<string | null>>();

function normalizeContentPath(contentPath: string | undefined): string | undefined {
    if (!contentPath) {
        return undefined;
    }
    return contentPath.endsWith('.html') ? contentPath : `${contentPath}.html`;
}

function fetchFragmentHtml(contentPath: string): Promise<string | null> {
    const cached = fragmentHtmlCache.get(contentPath);
    if (cached) {
        return cached;
    }

    const request = fetch(contentPath)
        .then((response) => response.text())
        .then((data) => {
            const html = new DOMParser().parseFromString(data, 'text/html');
            return html.body.innerHTML;
        })
        .catch((error) => {
            console.warn(error);
            fragmentHtmlCache.delete(contentPath);
            return null;
        });

    fragmentHtmlCache.set(contentPath, request);
    return request;
}

function initRegisterableComponents(root: HTMLElement) {
    for (const key in registerableComponentList) {
        const componentElements = root.querySelectorAll(key);
        if (componentElements.length > 0) {
            const componentClass = registerableComponentList[key];
            componentElements.forEach((componentElement) => {
                try {
                    new componentClass(componentElement).init();
                } catch (error) {
                    console.warn('Error initializing component:', key, error);
                }
            });
        }
    }
}

function activateChartScripts(root: HTMLElement) {
    root.querySelectorAll('.chart script').forEach((oldScript) => {
        const newScript = document.createElement('script');
        const src = oldScript.getAttribute('src');

        [...oldScript.attributes].forEach((attr) => {
            if (attr.name !== 'src') {
                newScript.setAttribute(attr.name, attr.value);
            }
        });

        // Classic scripts created with createElement are async unless this is set first.
        if (newScript.type !== 'module' && !oldScript.hasAttribute('async')) {
            newScript.async = false;
        }

        if (oldScript.textContent) {
            newScript.text = oldScript.textContent;
        }
        if (src) {
            newScript.src = src;
        }

        oldScript.replaceWith(newScript);
    });
}

function InsertFragment({ componentName, contentPath }: {
    componentName: string;
    contentPath: string | undefined;
}) {
    const resolvedPath = normalizeContentPath(contentPath);
    const fragmentRef = useRef<HTMLDivElement>(null);
    const [fragmentHtml, setFragmentHtml] = useState<string | null>(null);
    const [fragmentLoading, setFragmentLoading] = useState<boolean>(true);

    useEffect(() => {
        const root = fragmentRef.current;
        if (!root || !fragmentHtml) {
            return undefined;
        }

        initRegisterableComponents(root);
        activateChartScripts(root);

        if (!root.querySelector('.chart')) {
            return undefined;
        }

        let cancelled = false;
        const MAX_INTERVALS = 20;
        let intervals = 0;
        const interval = window.setInterval(() => {
            if (typeof window.futprintChartInit === 'function') {
                window.clearInterval(interval);
                if (!cancelled) {
                    window.futprintChartInit();
                }
            }
            intervals += 1;
            if (intervals > MAX_INTERVALS) {
                window.clearInterval(interval);
            }
        }, 500);

        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [fragmentHtml]);

    useEffect(() => {
        let cancelled = false;

        if (!resolvedPath) {
            setFragmentLoading(false);
            return;
        }

        fetchFragmentHtml(resolvedPath).then((html) => {
            if (cancelled) {
                return;
            }
            setFragmentHtml(html);
            setFragmentLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [resolvedPath]);

    return (
        <>
            {!fragmentLoading && fragmentHtml && (
                <div
                    ref={fragmentRef}
                    className={`${componentName}__section-fragment`}
                    dangerouslySetInnerHTML={{ __html: fragmentHtml }}
                />
            )}
        </>
    );
}

export default InsertFragment;
